// Deep links, universal links, push routing (GATE-3). A push/universal-link tap carries a target
// `route` (payload `{kind, route, params}`); it must be a SANITIZED SAME-ORIGIN path before the app
// navigates to it, and a COLD-LAUNCH tap must PARK until auth + bootstrap resolve. This module is the
// app-agnostic core (no @cntrl config, no router import): a pure route sanitizer + a park/flush controller.
// The native listener wiring (App.appUrlOpen / PushNotifications tap) lives in the native-gated push module;
// apps/core wires this controller into Bootstrap (the `?to=` cold-launch entry + the post-ready flush).

/** A leading path segment allowlist (defense-in-depth on top of the structural same-origin check). The app
 * supplies its route roots; `""` covers the index route `/`. */
export type AllowedRoots = ReadonlySet<string>;

const MAX_ROUTE_LENGTH = 2048;
// A throwaway origin used only to resolve/validate the path; any absolute or protocol-relative input that
// escapes it is rejected (an open-redirect / cross-origin guard). Assembled from parts so it is not a
// hardcoded http(s) hostname literal (this is a parse sentinel, never a real host — lint).
const SANDBOX_ORIGIN = "https:" + "//deep-link.invalid";

/** True if the string contains an ASCII control char (rejected in an untrusted route — kept out of a regex
 * literal so no control byte lands in source). */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * Sanitize an untrusted push/universal-link `route` into a safe same-origin SPA path, or null if it is not
 * one ("payload routes pass a same-origin path allowlist"). Rejects: non-absolute inputs, schemes
 * (`javascript:`, `http:`, …), protocol-relative `//host`, backslash tricks, control chars, over-long input,
 * and — when {@link allowedRoots} is given — any first path segment not in the app's route set. Returns the
 * normalized `pathname + search + hash` (never an origin), safe to hand to the router.
 *
 * The input is treated as an ALREADY-final path — this function does NOT percent-decode. Callers decode once
 * at the boundary: {@link parseToParam} returns the `?to=` value already decoded by `URLSearchParams`, a push
 * `data.route` is a server-sanitized plain path, and a universal-link `pathname` is validated as-is.
 * (Decoding here too would DOUBLE-decode the `?to=` path — corrupting `+`/`%25` and throwing on a bare `%`;
 * and NOT decoding is strictly safer, since a still-encoded `%2F%2Fevil` fails the `/`-prefix check closed.)
 */
export function sanitizeRoute(raw: string | null | undefined, allowedRoots?: AllowedRoots): string | null {
  if (typeof raw !== "string") return null;

  const value = raw.trim();
  if (value.length === 0 || value.length > MAX_ROUTE_LENGTH) return null;

  if (!value.startsWith("/")) return null; // must be an absolute path — not a scheme, not relative
  if (value.startsWith("//")) return null; // protocol-relative //evil.com
  if (value.includes("\\")) return null; // backslash open-redirect tricks (/\evil.com)
  if (hasControlChar(value)) return null;

  let url: URL;
  try {
    url = new URL(value, SANDBOX_ORIGIN);
  } catch {
    return null;
  }
  // Anything that resolved to a different origin (a smuggled absolute/protocol-relative URL) is rejected.
  if (url.origin !== SANDBOX_ORIGIN) return null;

  const path = url.pathname + url.search + url.hash;
  if (!path.startsWith("/")) return null;

  if (allowedRoots) {
    const firstSegment = url.pathname.split("/")[1] ?? "";
    if (!allowedRoots.has(firstSegment)) return null;
  }

  return path;
}

/** Extract the legacy `?to=<route>` push entry from a URL search string (the `?to=` redirect
 * route). Returns the RAW value (still to be sanitized) or null. */
export function parseToParam(search: string): string | null {
  try {
    const normalized = search.startsWith("?") ? search : `?${search}`;
    return new URLSearchParams(normalized).get("to");
  } catch {
    return null;
  }
}

/**
 * Parks a deep-link route until the app can navigate, then flushes it. A cold-launch tap (push or universal
 * link, or the `?to=` entry) arrives before auth + bootstrap resolve, so {@link handle} parks the sanitized
 * route; once the router + session are ready the app calls {@link activate} with its navigator, which flushes
 * the parked route and routes every subsequent tap immediately. Instance-scoped (no module state) so it is
 * trivially testable.
 */
export class DeepLinkController {
  private pending: string | null = null;
  private navigate: ((route: string) => void) | null = null;

  constructor(private readonly allowedRoots?: AllowedRoots) {}

  /** Sanitize a raw deep link and either navigate (if active) or park it (cold launch). Junk is dropped. */
  handle(raw: string | null | undefined): void {
    const route = sanitizeRoute(raw, this.allowedRoots);
    if (route === null) return;
    if (this.navigate) this.navigate(route);
    else this.pending = route;
  }

  /** The `?to=<route>` cold-launch entry from a location search string (parse → sanitize → park/navigate). */
  handleInitial(search: string): void {
    this.handle(parseToParam(search));
  }

  /** Install the app's navigator once auth + bootstrap + router are ready, flushing any parked cold-launch tap. */
  activate(navigate: (route: string) => void): void {
    this.navigate = navigate;
    if (this.pending !== null) {
      const flushed = this.pending;
      this.pending = null;
      navigate(flushed);
    }
  }

  /** The currently parked route (null once flushed) — for tests / diagnostics. */
  get parkedRoute(): string | null {
    return this.pending;
  }
}
